import { useMutation } from "@tanstack/react-query";
import { AppointmentPhase, Input, LabelledRow, Modal, Status, StatusDropdown } from "components";
import ChooseContact from "components/AddresseePicker/ChooseContact";
import CollapsibleSection from "components/DetailPage/CollapsibleSection";
import PublicationDropdown from "components/dropdowns/reactselect/PublicationDropdown";
import { useBookkeepingCurrency } from "components/exchangerate/hooks";
import LabelledDivRow from "components/LabelledDivRow";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import EditViewPanel from "components/panels/EditViewPanel";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import VenueModalPicker from "components/Picker/VenuePicker/VenueModalPicker";
import SelectProduct from "components/products/SelectProduct";
import { type ShowDates } from "components/showdatepicker/models";
import ShowDatePicker from "components/showdatepicker/ShowDatePicker";
import TimeOfPerformance from "components/showdatepicker/TimeOfPerformance";
import { ExclusivitySidePanel } from "exclusivityclause/ExcusivitySidePanel";
import { ExclusivityCheckFields } from "exclusivityclause/utils";
import TwoColumnLayout from "layout/TwoColumnLayout";
import moment from "moment";
import React from "react";
import { Button, Message } from "semantic-ui-react";
import ArtistAttendeePickerSection from "show/components/ArtistAttendeePicker/App";
import BookkeepingCurrency from "show/components/bookkeepingcurrency";
import ShowDateTime from "show/components/ShowDateTime";
import HotelPanel from "show/edit/Internal/HotelPanel";
import PromoterPanel from "show/edit/Internal/PromoterPanel";
import { routes } from "utilities/routing/generatedRoutes";
import { getRealStartEndDate } from "utilities/showDateUtils";
import CopyIntervals from "./components/CopyIntervals";
import RepetitionCalendar from "./components/RepetitionCalendar";
import RepetitionsDropdown from "./components/RepetitionsDropdown";
import { useLoaderData } from "react-router";
import { ProjectCodeInput } from "components/ProjectCodeInput";

const ShowCopyPage = () => {
  const viewModel = useLoaderData<server.viewmodel.ShowCopyFrontModel>();
  const { show } = viewModel;
  const [copyInterval, setCopyInterval] = React.useState<server.dto.CopyIntervals>("once");

  const [showDates, setShowDates] = React.useState<ShowDates>({
    startDisplayDate: moment.utc(show.dateTime.startDisplay),
    endDisplayDate: moment.utc(show.dateTime.endDisplay),
    timeOfPerformance: show.dateTime.customFallbackTime,
    mode: show.dateTime.mode
  });

  const { startRealDate } = getRealStartEndDate(showDates);

  const [dateHasError, setDateHasError] = React.useState(false);

  const [repetitions, setRepetitions] = React.useState(1);

  const [status, setStatus] = React.useState(show.status);
  const [nameOfEvent, setNameOfEvent] = React.useState(show.eventName ?? "");
  const [projectCode, setProjectCode] = React.useState(show.projectCode);
  const [publicationStatus, setPublicationStatus] = React.useState(show.publicationStatus);

  const [costCenter, setCostCenter] = React.useState(show.costCenter);
  const [costCenterHasError, setCostCenterHasError] = React.useState(false);

  const [owner, setOwner] = React.useState(show.owner);

  const [artist, setArtist] = React.useState<server.dto.ArtistSmall>(show.artist);
  const [attendees, setAttendees] = React.useState(show.attendees);

  const [promoter, setPromoter] = React.useState(show.promoter);
  const [promoterContact, setPromoterContact] = React.useState(show.promoterContact);

  const [venue, setVenue] = React.useState(show.venue);
  const [venueContact, setVenueContact] = React.useState(show.venueContact);

  const [hotel, setHotel] = React.useState(show.hotel);
  const [hotelContact, setHotelContact] = React.useState(show.hotelContact);

  const [products, setProducts] = React.useState(viewModel.products.collection);

  const [ownerError, setOwnerError] = React.useState(false);
  const [artistError, setArtistError] = React.useState(false);

  const [displayCreatedModal, setDisplayCreatedModal] = React.useState(false);

  const bookkeepingCurrency = useBookkeepingCurrency(show.bookkeeping, show.currency, show.exchangeRate, {
    useBaseCurrencyOnBookkeepingChange: false
  });

  const [exchangeRateError, setExchangeRateError] = React.useState(false);

  const selectedProducts = new Set(products.map(p => p.productId));

  const copyShowMutation = useMutation(window.CoreApi.Show.CopyShow);

  const costCenterRef = React.useRef<HTMLTableRowElement>(null);
  const datesRef = React.useRef<HTMLTableRowElement>(null);
  const ownerRef = React.useRef<HTMLTableRowElement>(null);
  const exchangeRateRef = React.useRef<HTMLTableRowElement>(null);
  const artistRef = React.useRef<HTMLTableRowElement>(null);

  const onSave = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    let hasErrors = false;
    let refToScrollTo: React.MutableRefObject<HTMLTableRowElement> = null;

    if (copyInterval === "once" && dateHasError) {
      hasErrors = true;
      refToScrollTo = datesRef;
    }

    if (site.currentEnvironment.hasCostCenters && costCenter == null) {
      setCostCenterHasError(true);
      hasErrors = true;
      refToScrollTo = costCenterRef;
    }

    if (owner == null) {
      setOwnerError(true);
      hasErrors = true;
      refToScrollTo = ownerRef;
    }

    if (bookkeepingCurrency.exchangeRate === 0) {
      setExchangeRateError(true);
      hasErrors = true;
      refToScrollTo = exchangeRateRef;
    }

    if (!viewModel.hasAccessToAllArtists && artist == null) {
      setArtistError(true);
      hasErrors = true;
      refToScrollTo = artistRef;
    }

    if (hasErrors) {
      refToScrollTo.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    await copyShowMutation.mutateAsync({
      artistId: artist?.artistId,
      attendees: attendees.map(a => a.contactId),
      bookkeepingId: bookkeepingCurrency.bookkeeping.bookkeepingId,
      costCenterId: costCenter?.costCenterId,
      currency: bookkeepingCurrency.currency,
      dateTime: {
        startDisplay: showDates.startDisplayDate.toDate(),
        endDisplay: showDates.endDisplayDate.toDate(),
        mode: showDates.mode,
        customFallbackTime: showDates.timeOfPerformance
      },
      eventName: nameOfEvent,
      exchangeRate: bookkeepingCurrency.exchangeRate,
      hotelCompanyId: hotel?.companyId,
      projectCode: projectCode,
      promoterCompanyId: promoter?.companyId,
      showId: show.showId,
      statusId: status.statusId,
      venueCompanyId: venue?.venueId,
      promoterContactId: promoterContact?.contactId,
      venueContactId: venueContact?.contactId,
      hotelContactId: hotelContact?.contactId,
      productIds: products.map(p => p.productId),
      copyRepetitions: repetitions,
      copyInterval,
      ownerUserId: owner.userId,
      publication: publicationStatus
    });

    setDisplayCreatedModal(true);
  };

  const { hotel: hotelFg, artist: artistFg, venue: venueFg, promoter: promoterFg } = site.currentEnvironment.functionGroups;

  React.useEffect(() => {
    if (exchangeRateError && bookkeepingCurrency.exchangeRate !== 0) {
      setExchangeRateError(false);
    }
  }, [bookkeepingCurrency.exchangeRate, exchangeRateError])

  const exclusivityCheckFields: ExclusivityCheckFields = {
    artistId: artist?.artistId,
    excludeShowId: show.showId,
    exclusivityClause: viewModel.exclusivityClause,
    showStartDisplay: showDates.startDisplayDate,
    showEndDisplay: showDates.endDisplayDate,
    venueLocation: venue?.location,
    venueId: venue?.venueId,
    appointmentPhase: status.appointmentPhase,
  }

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <EditViewPanel
        title={viewModel.pageTitle}>

        <form onSubmit={onSave} className="flex-column gap-bigger">
          <div className="content-section">
            <table className="s1_table bordergrid">
              <tbody>
                <LabelledRow bigPadding label={RESX.GeneralLabels.Title}>
                  {show.title}
                </LabelledRow>
                <LabelledRow bigPadding label={RESX.DateTime.lblDate}>
                  <ShowDateTime dateTime={show.dateTime} />
                </LabelledRow>
                <LabelledRow bigPadding label={RESX.Status.resxStatus}>
                  <div className="flex gap-small">
                    <AppointmentPhase value={show.status.appointmentPhase} displayLabel={false} />
                    <Status status={show.status} />
                  </div>
                </LabelledRow>
              </tbody>
            </table>
          </div>

          <CollapsibleSection
            title={RESX.GeneralLabels.Settings}
            uniqueRowId="settingsSection"
            collapsible="no">

            <LabelledDivRow label={RESX.Buttons.Copy_Imperative_Singular} bigPadding>
              <CopyIntervals
                value={copyInterval}
                onChange={setCopyInterval}
              />
            </LabelledDivRow>

            <table className="s1_table bordergrid gray">
              <tbody>
                {copyInterval === "once" && <>
                  <LabelledRow rowRef={datesRef} label={RESX.DateTime.lblDate} bigPadding>
                    <ShowDatePicker
                      dates={showDates}
                      onDatesChanged={(dates, hasErrors) => {
                        setShowDates(dates);
                        setDateHasError(hasErrors);
                      }}
                    />
                  </LabelledRow>

                  <LabelledRow label={RESX.Show.TimeOfPerformance}>
                    <TimeOfPerformance
                      showDates={showDates}
                      onTimeOfPerformanceChanged={(newTimeOfPerformance) => { setShowDates({ ...showDates, timeOfPerformance: newTimeOfPerformance }); }}
                    />
                  </LabelledRow>
                </>}
                {copyInterval !== "once" && <LabelledRow label={RESX.Planning.Repetitions} bigPadding>
                  <div className="margin-bottom-small">
                    <RepetitionsDropdown value={repetitions} onChange={setRepetitions} />
                  </div>
                  <RepetitionCalendar
                    repetitions={repetitions}
                    interval={copyInterval}
                    realStartDate={startRealDate}
                  />
                </LabelledRow>}

                <LabelledRow label={RESX.Status.resxStatus}>
                  <StatusDropdown
                    options={undefined}
                    onChange={setStatus}
                    value={status}
                    mainArea="show"
                  />
                </LabelledRow>

                <LabelledRow label={RESX.Show.NameOfEvent}>
                  <Input
                    fluid
                    value={nameOfEvent}
                    onChange={(_, { value }) => { setNameOfEvent(value); }}
                    maxLength={serverReference.constants.show.maxEventNameLength}
                  />
                </LabelledRow>

                <LabelledRow label={RESX.SelectionManager.Publication}>
                  <PublicationDropdown
                    value={publicationStatus}
                    onChange={setPublicationStatus}
                  />
                </LabelledRow>

                <BookkeepingCurrency
                  bookkeepingCurrency={bookkeepingCurrency}
                  exchangeRateError={exchangeRateError}
                  rowRef={exchangeRateRef}
                />

                {site.currentEnvironment.hasCostCenters && <LabelledRow rowRef={costCenterRef} id={"costCenterRow"} label={RESX.CostCenter.resxCostCenter}>
                  <CostCenterModalPicker
                    canCreateNew={false}
                    nonInPageEditing={{
                      onChange: cc => {
                        setCostCenter(cc);
                        setCostCenterHasError(false);
                      },
                      value: costCenter,
                      validatorKey: "costCenter",
                      required: true,
                      legacyHasError: costCenterHasError,
                    }}
                  />
                </LabelledRow>}

                {site.currentEnvironment.projectCode !== "hidden" && <LabelledRow label={RESX.Show.ProjectCode}>
                  <ProjectCodeInput
                    value={projectCode}
                    onChange={setProjectCode}
                    canCreateNew={false}
                  />
                </LabelledRow>}

                <LabelledRow label={RESX.GeneralLabels.Owner} rowRef={ownerRef}>
                  <UserModalPicker
                    canChooseDeactivated
                    canCreateNew={false}
                    nonInPageEditing={{
                      onChange: setOwner,
                      value: owner,
                      required: true,
                      legacyHasError: ownerError,
                      validatorKey: "ownerPicker",
                    }}
                  />
                </LabelledRow>

              </tbody>
            </table>
          </CollapsibleSection>

          {artistFg.enabled && <ArtistAttendeePickerSection
            validatorKey="artist"
            isRequired={!viewModel.hasAccessToAllArtists}
            rowRef={artistRef}
            artist={artist}
            attendees={attendees}
            onArtistChange={setArtist}
            onAttendeesChange={setAttendees}
            hasError={artistError}
            autoSelectDefaultAttendees={false}
          />}

          {venueFg.enabled && <CollapsibleSection
            title={site.currentEnvironment.functionGroups.venue.singular}
            uniqueRowId="showCopyVenue"
            collapsible="no"
            type="form">
            <LabelledDivRow label={site.currentEnvironment.functionGroups.venue.singular}>
              <div>
                <VenueModalPicker
                  canCreateNew
                  afterCreate="closeSidebar"
                  nonInPageEditing={{
                    onChange: v => setVenue(v),
                    value: venue,
                    validatorKey: "venue",
                    required: false,
                  }}
                />
              </div>
              {venue != null && <div className="grow1">
                <ChooseContact
                  companyId={venue.venueId}
                  selectedContact={venueContact}
                  onContactChanged={setVenueContact}
                />
              </div>}
            </LabelledDivRow>
          </CollapsibleSection>}

          {promoterFg.enabled && <CollapsibleSection
            title={site.currentEnvironment.functionGroups.promoter.singular}
            uniqueRowId="showCopyPromoter"
            collapsible="no"
            type="form">
            <PromoterPanel
              company={promoter}
              onCompanyChange={setPromoter}
              contact={promoterContact}
              onContactChange={setPromoterContact}
            />
          </CollapsibleSection>}



          {hotelFg.enabled && <CollapsibleSection
            title={site.currentEnvironment.functionGroups.hotel.singular}
            uniqueRowId="showCopyHotel"
            collapsible="startOpen"
            type="form">
            <HotelPanel
              company={hotel}
              onCompanyChange={setHotel}
              contact={hotelContact}
              onContactChange={setHotelContact}
            />
          </CollapsibleSection>}

          {viewModel.products.collection.length > 0 && <CollapsibleSection
            title={RESX.Product.Products}
            uniqueRowId="productsSection"
            collapsible="no">
            <table className="s1_table bordergrid gray">
              <tbody>
                <LabelledRow label={RESX.Product.Products}>
                  {viewModel.products.collection.map(prod => (
                    <SelectProduct
                      key={prod.productId}
                      product={prod}
                      checked={selectedProducts.has(prod.productId)}
                      onChecked={(product) => {
                        setProducts(prev => {
                          if (selectedProducts.has(product.productId)) {
                            return prev.filter(p => p.productId !== product.productId);
                          }
                          return [...prev, product];
                        });
                      }}
                    />
                  ))}
                </LabelledRow>
              </tbody>
            </table>
          </CollapsibleSection>}

          <div className="flex justify-end">
            {(copyShowMutation.data?.data.hasErrors || copyShowMutation.isError) &&
              <div
                className="grow1"
                style={{ color: "red" }}
              >
                {RESX.GeneralLabels.SomethingUnexpectedHappened}
              </div>}
            <div>
              <Button
                primary
                disabled={copyShowMutation.isLoading}
                type="submit">
                {RESX.Buttons.btnCopy}
              </Button>
            </div>
          </div>
        </form>

        <ExclusivitySidePanel exclusivityCheckFields={exclusivityCheckFields} />

        <Modal
          isOpen={displayCreatedModal}
          handleClose={() => { setDisplayCreatedModal(false); }}
          title={RESX.Buttons.Copy_Imperative_Singular}
        >
          <Message color="blue">
            {copyShowMutation.data?.data.data.numberOfShowsCreated > 1
              ? RESX.GeneralLabels.YNewItemsWereCreated.replace("{0}", copyShowMutation.data?.data.data.numberOfShowsCreated.toString())
              : RESX.GeneralLabels.XNewItemsWereCreated}
          </Message>
          <div className="grow1"></div>
          <div className="flex">
            <div className="grow1"></div>
            <div>
              <Button
                primary
                onClick={() => window.location.href = routes.show_Detail(copyShowMutation.data?.data.data.showIdToRedirectTo).absolute}
              >{RESX.Buttons.BtnOk}</Button>
            </div>
          </div>
        </Modal>
      </EditViewPanel >
    </TwoColumnLayout.LeftColumn >
    <TwoColumnLayout.RightColumn>
      <GeneralOptionsPanel
        links={[
          { text: RESX.Buttons.ListView, href: viewModel.listUrl, visible: true },
          { text: RESX.Buttons.DetailView, href: routes.show_Detail(show.showId).absolute, visible: true }
        ]}
      />
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout >;
};

export default ShowCopyPage;